import {clsx} from 'clsx'
import {memo} from 'react'

import {ROADMAP_DATE_COLUMN_WIDTH, ROADMAP_NUMBER_COLUMN_WIDTH} from '../../../components/roadmap/constants'
import {getCellFocusState} from '../../../components/table/get-cell-focus-state'
import {useTableNavigation} from '../../../components/table/navigation'
import {TableCell} from '../../../components/table/table-cell'
import {useIsCellBulkSelected} from '../../../components/table/table-cell-bulk-selection'
import type {TableDataType} from '../../../components/table/table-data-type'
import type {FocusCellElementType} from '../../../components/table/use-item-data'
import {ViewerPrivileges} from '../../../helpers/viewer-privileges'
import type {Cell} from '../../../hooks/tanstack-table/helpers/types'
import {useRoadmapSettings} from '../../../hooks/use-roadmap-settings'
import {useRoadmapScrollMargins} from '../hooks/use-roadmap-scroll-margins'
import sharedStyles from './roadmap-shared.module.css'
import styles from './roadmap-table-cells.module.css'
import {RoadmapTableColumnResizeProvider} from './roadmap-table-column-resize-provider'
import {RoadmapTableDragSash} from './roadmap-table-drag-sash'
import type {TestIdentifiersProps} from './roadmap-table-layout'

type RoadmapTitleCellProps = {
  number?: number
  focusCellElement: FocusCellElementType
  cell: Pick<Cell<TableDataType>, 'column' | 'row'>
  isLastCell: boolean
}

export const RoadmapTitleCell = memo(function RoadmapTitleCell({
  number,
  cell,
  focusCellElement,
  isLastCell,
}: RoadmapTitleCellProps & TestIdentifiersProps) {
  const {titleColumnWidth} = useRoadmapSettings()
  const {hasWritePermissions} = ViewerPrivileges()
  const scrollMargins = useRoadmapScrollMargins()

  return (
    <TableCell
      row={cell.row}
      column={cell.column}
      // Prefer dragging styles defined in <RoadmapRow /> until we can unify d&d implementations, and substitute row-dragger for RoadmapNumberCell
      isDragging={false}
      focusCellElement={focusCellElement}
      styles={{
        ...scrollMargins,
        maxWidth: titleColumnWidth,
        minWidth: titleColumnWidth,
      }}
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className={clsx('roadmap-table-cell', sharedStyles.Cell, styles.baseCell, styles.titleCell)}
    >
      {hasWritePermissions && isLastCell && (
        <RoadmapTableColumnResizeProvider>
          <RoadmapTableDragSash id={`RoadmapTableDragSash-${number}`} />
        </RoadmapTableColumnResizeProvider>
      )}
    </TableCell>
  )
})

type RoadmapNumberCellProps = {
  focusCellElement: FocusCellElementType
  cell: Cell<TableDataType>
  isDragging: boolean
}

export const RoadmapNumberCell = memo(function RoadmapNumberCell({
  cell,
  focusCellElement,
  isDragging,
}: RoadmapNumberCellProps & TestIdentifiersProps) {
  return (
    <TableCell
      row={cell.row}
      column={cell.column}
      isDragging={isDragging}
      focusCellElement={focusCellElement}
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className={clsx('roadmap-table-cell', sharedStyles.Cell, styles.baseCell, styles.numberCell)}
    />
  )
})

type RoadmapTimeSpanCellsProps = {
  number?: number
  focusCellElement: FocusCellElementType
  startCell: Cell<TableDataType> | undefined
  endCell: Cell<TableDataType> | undefined
}

export const RoadmapTimeSpanCells = memo(function RoadmapTimeSpanCells({
  number,
  startCell,
  endCell,
  focusCellElement,
}: RoadmapTimeSpanCellsProps & TestIdentifiersProps) {
  const {hasWritePermissions} = ViewerPrivileges()
  const {titleColumnWidth} = useRoadmapSettings()

  const hasEndCell = endCell && startCell !== endCell
  const endDateLeft = startCell ? ROADMAP_DATE_COLUMN_WIDTH : 0

  const {
    state: {focus},
  } = useTableNavigation()

  const {isFocused: isStartCellFocused} = startCell ? getCellFocusState(startCell, focus) : {isFocused: false}
  const {isFocused: isEndCellFocused} = endCell ? getCellFocusState(endCell, focus) : {isFocused: false}
  const isStartBulkSelected = useIsCellBulkSelected(startCell)
  const isEndBulkSelected = useIsCellBulkSelected(endCell)

  return (
    <>
      {startCell && (
        <TableCell
          row={startCell.row}
          column={startCell.column}
          // Prefer dragging styles defined in <RoadmapRow /> until we can unify d&d implementations, and substitute row-dragger for RoadmapNumberCell
          isDragging={false}
          focusCellElement={focusCellElement}
          styles={{
            maxWidth: ROADMAP_DATE_COLUMN_WIDTH,
            minWidth: ROADMAP_DATE_COLUMN_WIDTH,
            left: ROADMAP_NUMBER_COLUMN_WIDTH + titleColumnWidth,
          }}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx('roadmap-table-cell', sharedStyles.Cell, styles.baseCell, styles.dateCell)}
        >
          {!hasEndCell && hasWritePermissions && (
            <RoadmapTableColumnResizeProvider>
              {!isStartBulkSelected && !isStartCellFocused && (
                <RoadmapTableDragSash id={`RoadmapTableDragSash-${number}`} />
              )}
            </RoadmapTableColumnResizeProvider>
          )}
        </TableCell>
      )}
      {hasEndCell && (
        <TableCell
          row={endCell.row}
          column={endCell.column}
          // Prefer dragging styles defined in <RoadmapRow /> until we can unify d&d implementations, and substitute row-dragger for RoadmapNumberCell
          isDragging={false}
          focusCellElement={focusCellElement}
          styles={{
            maxWidth: ROADMAP_DATE_COLUMN_WIDTH,
            minWidth: ROADMAP_DATE_COLUMN_WIDTH,
            left: ROADMAP_NUMBER_COLUMN_WIDTH + titleColumnWidth + endDateLeft,
          }}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx('roadmap-table-cell', sharedStyles.Cell, styles.baseCell, styles.dateCell)}
        >
          {hasWritePermissions && (
            <RoadmapTableColumnResizeProvider>
              {!isEndBulkSelected && !isEndCellFocused && (
                <RoadmapTableDragSash id={`RoadmapTableDragSash-${number}`} />
              )}
            </RoadmapTableColumnResizeProvider>
          )}
        </TableCell>
      )}
    </>
  )
})
